import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { CaretRightIcon, FolderIcon, HouseIcon } from '@phosphor-icons/react';
import { getLibraryContents } from 'services/library';
import type { Folder } from 'types/library';

interface Props {
    open: boolean;
    itemName: string;
    currentFolderId: number | null;
    onConfirm: (targetFolderId: number | null) => Promise<void>;
    onClose: () => void;
}

interface BreadcrumbItem {
    id: number | null;
    name: string;
}

const MoveModal: React.FC<Props> = ({ open, itemName, currentFolderId, onConfirm, onClose }) => {
    const [folders, setFolders] = useState<Folder[]>([]);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([{ id: null, name: 'Thư viện' }]);
    const [selected, setSelected] = useState<number | null>(undefined as any);
    const [loading, setLoading] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const browseFolderId = breadcrumb[breadcrumb.length - 1].id;

    useEffect(() => {
        if (!open) return;
        setBreadcrumb([{ id: null, name: 'Thư viện' }]);
        setSelected(undefined as any);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        getLibraryContents(browseFolderId)
            .then(data => setFolders(data.folders))
            .catch(() => setFolders([]))
            .finally(() => setLoading(false));
    }, [open, browseFolderId]);

    const navigateInto = (folder: Folder) => {
        setBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name }]);
        setSelected(undefined as any);
    };

    const navigateTo = (index: number) => {
        setBreadcrumb(prev => prev.slice(0, index + 1));
        setSelected(undefined as any);
    };

    const handleConfirm = async () => {
        // selected === undefined means no folder chosen → use current browse level
        const targetId = selected !== undefined ? selected : browseFolderId;
        setConfirming(true);
        try {
            await onConfirm(targetId);
        } finally {
            setConfirming(false);
        }
    };

    const isCurrentLocation = (folder: Folder) => folder.id === currentFolderId;

    return (
        <Modal
            title={`Di chuyển "${itemName}"`}
            open={open}
            onCancel={onClose}
            onOk={handleConfirm}
            okText="Di chuyển đến đây"
            cancelText="Hủy"
            okButtonProps={{ loading: confirming }}
            width={440}
        >
            {/* Breadcrumb */}
            <div className="mt-3 mb-2 flex items-center gap-1 flex-wrap">
                {breadcrumb.map((item, index) => (
                    <React.Fragment key={index}>
                        {index > 0 && <CaretRightIcon size={12} className="text-text-muted" />}
                        <button
                            onClick={() => navigateTo(index)}
                            className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition hover:bg-bg-hover ${index === breadcrumb.length - 1 ? 'font-semibold text-text-primary' : 'text-text-secondary hover:text-primary'}`}
                        >
                            {index === 0 && <HouseIcon size={12} />}
                            {item.name}
                        </button>
                    </React.Fragment>
                ))}
            </div>

            {/* Folder list */}
            <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
                {loading ? (
                    <div className="flex items-center justify-center py-8 text-sm text-text-muted">Đang tải...</div>
                ) : folders.length === 0 ? (
                    <div className="flex items-center justify-center py-8 text-sm text-text-muted">Không có thư mục con</div>
                ) : (
                    folders.map(folder => {
                        const isCurrent = isCurrentLocation(folder);
                        const isSelected = selected === folder.id;
                        return (
                            <div
                                key={folder.id}
                                className={`flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0 transition-colors ${isCurrent ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-bg-hover'} ${isSelected ? 'bg-primary/5' : ''}`}
                                onClick={() => !isCurrent && setSelected(folder.id)}
                            >
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: folder.color + '33' }}>
                                    <FolderIcon size={15} style={{ color: folder.color }} weight="fill" />
                                </span>
                                <span className="flex-1 truncate text-sm font-medium text-text-primary">{folder.name}</span>
                                {isCurrent && <span className="text-xs text-text-muted">Hiện tại</span>}
                                {!isCurrent && (
                                    <button
                                        className="text-xs text-primary hover:underline shrink-0"
                                        onClick={e => { e.stopPropagation(); navigateInto(folder); }}
                                    >
                                        Mở
                                    </button>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Destination hint */}
            <p className="mt-2 text-xs text-text-muted">
                Đích đến: <span className="font-medium text-text-primary">
                    {selected != null
                        ? folders.find(f => f.id === selected)?.name
                        : breadcrumb[breadcrumb.length - 1].name}
                </span>
            </p>
        </Modal>
    );
};

export default MoveModal;
